import { CheckIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetClientProfile } from '@/api/clients/get-client-profile'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { StatusTabs } from '@/components/ui/status-tabs'
import { Typography } from '@/components/ui/typography'
import { DirtyFieldChange } from '@/hooks'
import { UserRoleEnum } from '@/models/features/user.models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'

import { DormakabaTab } from './dormakaba/dormakaba-tab'
import { IglooTab } from './igloo/igloo-tab'
import {
  canEditKeylessAccess,
  getKeylessTabLabels,
  KEYLESS_TAB,
  KeylessTabKey,
  SALTO_SPACE_ALWAYS_AVAILABLE_BUILDING_IDS,
} from './keyless-access.constants'
import * as S from './keyless-access.styled'
import { EnabledTab, KeylessAccessProps } from './keyless-access.types'
import { KeylessAccessSkeleton } from './keyless-access-skeleton'
import { PointCentralTab } from './point-central/point-central-tab'
import { RemoteLockTab } from './remote-lock/remote-lock-tab'
import { SaltoSpaceTab } from './salto-space/salto-space-tab'
import { SeamVisionlineTab } from './seam-visionline/seam-visionline-tab'
import { SwiftlaneTab } from './swiftlane/swiftlane-tab'

export const KeylessAccess = ({ clientId, onDirtyChange }: KeylessAccessProps) => {
  const { t } = useTranslation(['clients', 'common'])
  const userRole = useSelector(selectUserRole)
  const selectedBuilding = useSelector(selectActiveBuilding)
  const { data: clientData, isLoading } = useGetClientProfile({ clientId })

  const [isEditing, setIsEditing] = useState(false)
  const [isTabLoading, setIsTabLoading] = useState(false)
  const [isTabConnected, setIsTabConnected] = useState(false)
  const [activeTab, setActiveTab] = useState<KeylessTabKey | null>(null)
  const [editSessionKey, setEditSessionKey] = useState(0)
  const tabSaveFnRef = useRef<(() => void) | null>(null)
  const tabGetChangesFnRef = useRef<(() => DirtyFieldChange[]) | null>(null)
  const registerSave = useCallback((fn: (() => void) | null) => {
    tabSaveFnRef.current = fn
  }, [])
  const registerGetChanges = useCallback((fn: (() => DirtyFieldChange[]) | null) => {
    tabGetChangesFnRef.current = fn
  }, [])

  useEffect(() => {
    onDirtyChange?.(isEditing, isEditing ? () => tabGetChangesFnRef.current?.() ?? [] : undefined)
  }, [isEditing])

  if (userRole === UserRoleEnum.enum.Trainer) return null
  if (isLoading) return <KeylessAccessSkeleton />
  if (!clientData) return null

  const buildingId = clientData.buildingId.id
  const {
    dormakabaAvailable,
    remoteLockAvailable,
    saltoSpaceAvailable,
    swiftlaneAvailable,
    visionlineAvailable,
    pointCentralAvailable,
    iglooWorkAlgoPinAvailable,
    iglooWorkCustomPinAvailable,
  } = clientData.buildingId

  const isSaltoSpaceAvailable =
    saltoSpaceAvailable ||
    SALTO_SPACE_ALWAYS_AVAILABLE_BUILDING_IDS.includes(
      buildingId as (typeof SALTO_SPACE_ALWAYS_AVAILABLE_BUILDING_IDS)[number],
    )

  const keylessTabLabels = getKeylessTabLabels(t)

  const enabledTabs: EnabledTab[] = [
    dormakabaAvailable && { label: keylessTabLabels.dormakaba, value: 'dormakaba' },
    remoteLockAvailable && {
      label: keylessTabLabels['remote-lock'],
      value: 'remote-lock',
    },
    isSaltoSpaceAvailable && {
      label: keylessTabLabels['salto-space'],
      value: 'salto-space',
    },
    swiftlaneAvailable && { label: keylessTabLabels.swiftlane, value: 'swiftlane' },
    visionlineAvailable && {
      label: keylessTabLabels['seam-visionline'],
      value: 'seam-visionline',
    },
    pointCentralAvailable && {
      label: keylessTabLabels['point-central'],
      value: 'point-central',
    },
    // Note: either PIN flag opens the tab, and each block inside it follows its own flag. A
    // building may buy the resident's custom PIN without the staff-issued algoPIN, and gating the
    // tab on the algoPIN alone would leave that building with no way to reach the custom PIN.
    (iglooWorkAlgoPinAvailable || iglooWorkCustomPinAvailable) && {
      label: keylessTabLabels.igloo,
      value: 'igloo',
    },
  ].filter(Boolean) as EnabledTab[]

  const hasEnabledTabs = enabledTabs.length > 0
  const currentTab = hasEnabledTabs ? (activeTab ?? enabledTabs[0].value) : null

  const canEdit = hasEnabledTabs && canEditKeylessAccess(userRole)

  const handleTabChange = (tab: KeylessTabKey) => {
    // Note: re-selecting the active tab must be a no-op. Resetting the flags here while the tab
    // component stays mounted leaves them stale — it only reports its connection state when its
    // own inputs change, so the Edit button disappears until the user switches away and back.
    if (tab === currentTab) return
    setActiveTab(tab)
    setIsEditing(false)
    setIsTabLoading(false)
    setIsTabConnected(false)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditSessionKey((k) => k + 1)
  }

  const handleSaveChanges = () => {
    if (tabSaveFnRef.current) {
      tabSaveFnRef.current()
    } else {
      setIsEditing(false)
    }
  }

  const clientEmail = clientData.user.email
  const clientName = `${clientData.user.firstName} ${clientData.user.lastName}`.trim()
  const clientApartment = clientData.apartment

  const tabProps = {
    buildingId,
    clientApartment,
    clientEmail,
    clientId,
    clientName,
    clientUserId: String(clientData.user.id),
    isEditing,
    onConnectionChange: setIsTabConnected,
    onEditingChange: setIsEditing,
    onLoadingChange: setIsTabLoading,
    onRegisterGetChanges: registerGetChanges,
    onRegisterSave: registerSave,
  }

  const renderTab = () => {
    switch (currentTab) {
      case KEYLESS_TAB.DORMAKABA:
        return <DormakabaTab key={editSessionKey} {...tabProps} />
      case KEYLESS_TAB.REMOTE_LOCK:
        return (
          <RemoteLockTab
            key={editSessionKey}
            {...tabProps}
            remoteLockFobAvailable={clientData.buildingId.remoteLockFobAvailable ?? false}
          />
        )
      case KEYLESS_TAB.SALTO_SPACE:
        return (
          <SaltoSpaceTab
            key={editSessionKey}
            {...tabProps}
            isKeylessAccessSuspended={clientData.isKeylessAccessSuspended ?? false}
            leaseStart={clientData.leaseStart}
            moveInDate={clientData.moveInDate}
            saltoSpaceFobsAvailable={clientData.buildingId.saltoSpaceFobsAvailable ?? false}
          />
        )
      case KEYLESS_TAB.SWIFTLANE:
        return <SwiftlaneTab key={editSessionKey} {...tabProps} />
      case KEYLESS_TAB.SEAM_VISIONLINE:
        return <SeamVisionlineTab key={editSessionKey} {...tabProps} canEdit={canEdit} />
      case KEYLESS_TAB.POINT_CENTRAL:
        return (
          <PointCentralTab
            key={editSessionKey}
            {...tabProps}
            buildingId={selectedBuilding?.id ?? buildingId}
          />
        )
      case KEYLESS_TAB.IGLOO:
        return <IglooTab key={editSessionKey} {...tabProps} canEdit={canEdit} />
    }
  }

  return (
    <S.ContentCard>
      <S.SectionHeader>
        <Typography size="heading-small" as="h2">
          {t('clients:keylessAccess.title')}
        </Typography>
        <Flex gap={8}>
          {isEditing ? (
            <>
              <Button size="small" use="secondary" disabled={isTabLoading} onClick={handleCancel}>
                {t('common:actions.cancel')}
              </Button>
              <Button size="small" disabled={isTabLoading} onClick={handleSaveChanges}>
                {t('common:actions.saveChanges')}
                <Icon icon={CheckIcon} size="1.6rem" />
              </Button>
            </>
          ) : (
            canEdit &&
            // Point Central is the only tab that doesn't report its connection state via
            // onConnectionChange, so gating it on isTabConnected would leave Edit permanently
            // hidden. Every other tab (besides Dormakaba, excluded below) does report it.
            (currentTab === KEYLESS_TAB.POINT_CENTRAL || isTabConnected) &&
            currentTab !== KEYLESS_TAB.DORMAKABA && (
              <Button size="small" disabled={isTabLoading} onClick={() => setIsEditing(true)}>
                {t('common:actions.edit')}
                <Icon icon={PencilSimpleIcon} size="1.6rem" />
              </Button>
            )
          )}
        </Flex>
      </S.SectionHeader>

      {currentTab ? (
        <>
          <StatusTabs options={enabledTabs} value={currentTab} onChange={handleTabChange} />
          {renderTab()}
        </>
      ) : (
        // Note: keylessAccessAvailable (the building's parent toggle - gates sidebar/route
        // visibility, see client-profile.utils.ts's getModuleAvailability) is a separate concern
        // from whether any *specific* provider under it is actually configured for this building.
        // A building can have the parent toggle on with every individual provider off, which is
        // the case this renders for - see EL-14186 Figma for the empty-state spec.
        <S.TabContent>
          <EmptyState title={t('clients:keylessAccess.emptyState.title')} />
        </S.TabContent>
      )}
    </S.ContentCard>
  )
}
